import React, { useEffect, useState } from "react";
import { IndianRupee, ShoppingBag, Clock, Timer, Trophy, ListChecks } from "lucide-react";
import { api } from "../../lib/api";
import { compact, rupee } from "../../lib/format";
import { StatCard, Panel, DataTable, EntitySelect } from "./ui";
import { Header, Loading } from "./CorporateDashboard";
import { HourlyChart } from "./MallDashboard";

function scopedRestaurants(ctx, user) {
  let list = ctx.restaurants;
  if (["restaurant_admin", "restaurant_operator"].includes(user.role)) {
    list = list.filter((r) => (user.restaurant_ids || []).includes(r.id));
  } else if (user.role === "restaurant_group_admin") {
    list = list.filter((r) => r.group_id === user.group_id);
  }
  return list;
}

export default function RestaurantDashboard({ ctx, user }) {
  const rests = scopedRestaurants(ctx, user);
  const [rid, setRid] = useState(rests[0]?.id);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!rid) return;
    setData(null);
    api.get(`/dashboard/restaurant/${rid}`).then((r) => setData(r.data));
  }, [rid]);

  const selector = (
    <EntitySelect label="Restaurant" value={rid} onChange={setRid} testid="restaurant-select"
      options={rests.map((r) => ({ value: r.id, label: `${r.name} · ${r.foodcourt_name}` }))} />
  );

  if (!rid) return <Header title="Restaurant" sub="No restaurant assigned" />;
  if (!data) return <div className="space-y-6"><Header title="Restaurant" right={selector} /><Loading /></div>;
  const m = data.metrics;

  return (
    <div className="space-y-6">
      <Header title={data.restaurant?.name} sub={`${data.restaurant?.cuisine} · ${data.restaurant?.foodcourt_name}`} right={selector} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={IndianRupee} label="Today Sales" value={compact(m.sales)} testid="stat-rest-sales" />
        <StatCard icon={ShoppingBag} label="Orders" value={m.orders} sub={`AOV ${rupee(m.aov)}`} accent="blue" />
        <StatCard icon={ListChecks} label="Pending Orders" value={data.pending_orders} accent="gold" />
        <StatCard icon={Timer} label="Avg Prep Time" value={`${data.avg_prep_min}m`} accent="emerald" />
      </div>

      {data.best_item && (
        <div className="card-surface rounded-2xl p-5 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl gold-gradient flex items-center justify-center"><Trophy className="text-slate-950" size={22} /></div>
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Best Selling Item Today</div>
            <div className="font-display font-bold text-lg">{data.best_item.name}</div>
            <div className="text-xs text-slate-400">{data.best_item.qty} sold · {rupee(data.best_item.revenue)}</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Hourly Sales"><HourlyChart data={data.hourly} /></Panel>
        <Panel title="Top Items">
          <DataTable testid="rest-item-table"
            columns={[
              { key: "name", label: "Item" },
              { key: "qty", label: "Qty" },
              { key: "revenue", label: "Revenue", render: (r) => rupee(r.revenue) },
            ]} rows={data.top_items} />
        </Panel>
      </div>
    </div>
  );
}
